import { TextOverflowTooltip } from '@ipfabric/ui-components/Tooltip/TextOverflowTooltip';
import {
  Tooltip,
  isOverflowingContent,
} from '@ipfabric/ui-components/Tooltip/Tooltip';
import type { Security } from '@ipfabric/utils';
import { somePass } from '@ipfabric/utils/fp/predicate';
import classNames from 'classnames';
import {
  SecurityActionLabel,
  getActionName,
} from 'components/SecuritySchema/SecurityActionLabel';
import { isHighlightedChainItem } from 'components/SecuritySchema/helpers/isHighlightedChainItem';
import type {
  RuleNode,
  SecuritySchemaNodeComponentProps,
} from 'components/SecuritySchema/model/SecuritySchemaNode';
import { useLocale } from 'containers/App/LocaleContext';
import type { FC } from 'react';

const getActionNameMaxLength = (action: Security.Action) =>
  action.type === 'exec' ? 10 : 15;

const isActionNameTruncated = (action: Security.Action) => () =>
  getActionName(action).length > getActionNameMaxLength(action);

const getActionTooltip = (action: Security.Action) =>
  `${getActionName(action)}${
    action.type === 'exec' ? `: ${action.chainName}` : ''
  }`;

/**
 * Cloud rules carry the vendor's evaluation order; on-prem chains go by array
 * order and have none. Skipped when it is also the heading — a cloud rule with
 * no name or id goes by its priority, and the box would print it twice.
 */
const showPriority = (node: RuleNode): boolean =>
  node.priority !== undefined && node.name !== String(node.priority);

export const CRuleNode: FC<SecuritySchemaNodeComponentProps<RuleNode>> = ({
  context,
  node,
}) => {
  const msg = useLocale();
  const msgRule = msg.securitySchema.rule;
  return (
    <div
      className={classNames(
        'security-tree-node security-tree-node--rule',
        isHighlightedChainItem(node, context) &&
          'security-tree-node--highlighted',
      )}
      // A data attribute rather than a ref: collecting refs across the tree to
      // scroll onto one node would be tricky, and `querySelector` from an
      // ancestor inside this tree instance cannot reach another instance's node.
      data-security-rule-index={node.pageIndex}
    >
      {node.defaultAction ? (
        <p className='security-tree-node__heading'>
          {msg.securitySchema.ruleChain.defaultAction}
        </p>
      ) : (
        <>
          {node.name && (
            <TextOverflowTooltip as='p' className='security-tree-node__heading'>
              {node.name}
            </TextOverflowTooltip>
          )}
          <p>
            {`${msgRule.status.label}: `}
            {msgRule.status[node.active ? 'active' : 'inactive']}
            <i
              className={classNames(
                'fa',
                node.active ? 'fa-check' : 'fa-xmark',
              )}
            />
          </p>
          {showPriority(node) && (
            <p>{`${msgRule.priority}: ${node.priority}`}</p>
          )}
        </>
      )}
      <Tooltip
        content={getActionTooltip(node.action)}
        shouldShow={somePass([
          isOverflowingContent,
          isActionNameTruncated(node.action),
        ])}
      >
        <p className='truncate'>
          {`${msgRule.action}: `}
          <SecurityActionLabel
            action={node.action}
            dispatch={context.dispatch}
            securityType={context.securityType}
            treeScrollContainerRef={context.treeScrollContainerRef}
            truncateActionNameTo={getActionNameMaxLength(node.action)}
          />
        </p>
      </Tooltip>
    </div>
  );
};
